import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Custom hook to handle intelligent scroll restoration for iOS Safari compatibility.
 * - Preserves scroll position on reload and back/forward navigation
 * - Resets scroll to top only when navigating to new pages
 */
export const useScrollRestoration = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPathname = useRef<string>("");

  useEffect(() => {
    const currentPathname = location.pathname;

    // Only reset scroll when navigating to a different page (not back/forward or reload)
    const shouldResetScroll =
      navigationType === "PUSH" && // New navigation (not back/forward)
      previousPathname.current !== "" && // Not initial load
      previousPathname.current !== currentPathname; // Different page

    if (shouldResetScroll) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        // Multiple scroll attempts for iOS Safari reliability
        const scrollToTop = () => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant",
          });
        };

        // Immediate scroll
        scrollToTop();

        // iOS Safari fallback - multiple attempts with different timings
        setTimeout(() => {
          if (window.scrollY > 0) {
            scrollToTop();
          }
        }, 10);

        setTimeout(() => {
          if (window.scrollY > 0) {
            // Force scroll with direct method
            window.scrollTo(0, 0);
          }
        }, 50);

        setTimeout(() => {
          if (window.scrollY > 0) {
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          }
        }, 100);
      });
    }

    // Update previous pathname for next navigation
    previousPathname.current = currentPathname;
  }, [location.pathname, navigationType]);
};

